import { useEffect, useState, useMemo } from 'react'
import { useDepartment } from '../context/DepartmentContext'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { ChefHat, CakeSlice, Store, ShoppingBag, Lock, Loader, Sun, Moon } from 'lucide-react'

import { ADMIN_EMAILS } from '../config/adminConfig'

export default function DepartmentSelector() {
  const { departments, selectDepartment } = useDepartment()
  const { user, userProfile } = useAuth()
  const { isDark, toggleTheme } = useTheme()
  const [seeding, setSeeding] = useState(false)

  // Hardcoded admin bypass — works even when Firestore is offline
  const isHardcodedAdmin = ADMIN_EMAILS.includes(user?.email)
  const isAdmin = isHardcodedAdmin || userProfile?.role === 'admin'
  const allowedDepts = userProfile?.allowedDepartments || []
  const visibleDepartments = useMemo(() => {
    if (isAdmin) return departments
    if (allowedDepts.length === 0 && userProfile?.isApproved) return departments
    return departments.map(d => ({
      ...d,
      isActive: d.isActive && allowedDepts.includes(d.id),
    }))
  }, [departments, allowedDepts, userProfile, isAdmin])

  useEffect(() => {}, [])

  // Auto-select if user only has access to one department
  useEffect(() => {
    const activeDepts = visibleDepartments.filter(d => d.isActive)
    if (activeDepts.length === 1 && !seeding) {
      selectDepartment(activeDepts[0].name)
    }
  }, [visibleDepartments, seeding])

  // Group departments by business
  const businessGroups = useMemo(() => {
    const groups = {}
    for (const dept of visibleDepartments) {
      const biz = dept.businessId || 'default'
      if (!groups[biz]) {
        groups[biz] = {
          businessId: biz,
          businessName: dept.businessName || biz,
          businessLocation: dept.businessLocation || '',
          departments: [],
        }
      }
      groups[biz].departments.push(dept)
    }
    // Only show business panels where user has access to at least one dept
    return Object.values(groups).filter(g => g.departments.some(d => d.isActive))
  }, [visibleDepartments])

  const DEPT_ICONS = { kitchen: ChefHat, bakery: CakeSlice, tuckshop: ShoppingBag, yawehs: CakeSlice }

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4 relative">
      <button
        onClick={toggleTheme}
        className="absolute top-4 right-4 text-gray-500 hover:text-white p-2 rounded-lg transition-colors"
        title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      >
        {isDark ? <Sun size={20} /> : <Moon size={20} />}
      </button>
      <div className="w-full max-w-2xl">
        <div className="text-center mb-10">
          <div className="mb-2">
            <img src="/logo.jpg" alt="Desres Kitchen" className="inline-block w-16 h-16 object-contain" />
          </div>
          <p className="text-gray-400 text-sm">Select Department</p>
        </div>

        {seeding && (
          <div className="flex items-center justify-center gap-2 mb-6 text-brand-400 text-sm">
            <Loader size={16} className="animate-spin" />
            Loading historical data...
          </div>
        )}

        <div className="space-y-6">
          {businessGroups.map(biz => (
            <div key={biz.businessId} className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6">
              <div className="mb-5">
                <h2 className="text-lg font-bold text-white">{biz.businessName}</h2>
                {biz.businessLocation && (
                  <p className="text-xs text-gray-500">{biz.businessLocation}</p>
                )}
              </div>
              <div className="flex flex-wrap gap-4">
                {biz.departments.filter(d => d.isActive).map(dept => {
                  const Icon = DEPT_ICONS[dept.name] || Store
                  return (
                    <button
                      key={dept.name}
                      onClick={() => selectDepartment(dept.name)}
                      className="relative group w-40 rounded-xl border-2 border-gray-700 hover:border-brand-500 hover:bg-gray-800/50 p-6 transition-all cursor-pointer"
                    >
                      <div className="flex flex-col items-center gap-3">
                        <Icon size={36} className="text-brand-400" />
                        <span className="text-sm font-bold text-white">{dept.displayName}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-900 text-green-400">
                          Active
                        </span>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        {businessGroups.length === 0 && (
          <p className="text-gray-600 text-sm text-center mt-8 italic">
            You don't have access to any departments. Contact admin for access.
          </p>
        )}
      </div>
    </div>
  )
}
